import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import axios from "axios";

const NavBar = () => {
  const [user] = useAuthState(auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Cache keys for localStorage
  const CACHE_KEYS = {
    AUTH_STATE: "app_auth_state",
    USER_DATA: "app_user_data",
  };

  const [logoData, setLogoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogoData = async () => {
      try {
        const response = await axios.get("https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/nav-logo");
        setLogoData(response.data.data);
      } catch (err) {
        setError("Failed to load logo");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogoData();
  }, []);

  // Handle Firebase auth state changes
  useEffect(() => {
    if (!loading) {
      setAuthLoading(false);
      // Cache auth state
      try {
        const authStateData = {
          hasUser: !!user,
          timestamp: Date.now(),
          userEmail: user?.email || null,
        };
        localStorage.setItem(
          CACHE_KEYS.AUTH_STATE,
          JSON.stringify(authStateData)
        );
      } catch (error) {
        console.error("Error caching auth state:", error);
      }
    }
  }, [user, loading]);

  const handleSignout = async () => {
    try {
      await signOut(auth);
      // Clear all cached data on logout
      Object.values(CACHE_KEYS).forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error clearing ${key} from localStorage:`, error);
        }
      });
      setUserData(null);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);
    // Close mobile menu when opening user dropdown
    if (mobileMenuVisible) {
      setMobileMenuVisible(false);
    }
  };

  const closeUserDropdown = () => {
    setUserDropdownOpen(false);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
    // Close user dropdown when opening mobile menu
    if (userDropdownOpen) {
      setUserDropdownOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close user dropdown if clicking outside
      if (
        userDropdownOpen &&
        !event.target.closest(".user-dropdown-container") &&
        !event.target.closest(".user-avatar-button")
      ) {
        closeUserDropdown();
      }

      // Close mobile menu if clicking outside
      if (
        mobileMenuVisible &&
        !event.target.closest(".mobile-menu") &&
        !event.target.closest(".mobile-menu-button")
      ) {
        setMobileMenuVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen, mobileMenuVisible]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(" ")
        .map((name) => name[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    }
    return user?.email?.substring(0, 2).toUpperCase() || "U";
  };

  // Fetch user data with caching
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setUserData(null);
        // Clear cached user data when no user
        try {
          localStorage.removeItem(CACHE_KEYS.USER_DATA);
        } catch (error) {
          console.error("Error clearing cached user data:", error);
        }
        return;
      }
      try {
        const response = await fetch(
          `https://ecommerce-product-05-19692c57dcd1.herokuapp.com/users?userEmail=${user.email}`
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const userData = data[0];
          setUserData(userData);
          // Cache user data
          try {
            localStorage.setItem(
              CACHE_KEYS.USER_DATA,
              JSON.stringify(userData)
            );
          } catch (error) {
            console.error("Error caching user data:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    // Only fetch user data if we have a user and either no cached data or cached data is stale
    if (user?.email) {
      const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
      const shouldFetch = !cachedUserData || !userData;
      if (shouldFetch) {
        fetchUserData();
      }
    }
  }, [user]);

  // Render logo with proper null checks
  const renderLogo = (className = "") => {
    if (loading) {
      return <div className={`logo-placeholder ${className}`}>Loading...</div>;
    }
    if (error || !logoData || !logoData.isActive) {
      // Fallback to default logo
      return (
        <img
          src="/assets/img/logo/white-logo-2.svg"
          alt="logo-img"
          className={className}
        />
      );
    }
    return (
      <img
        src={logoData.imageUrl || "/assets/img/logo/white-logo-2.svg"}
        alt={logoData.altText || "Company Logo"}
        className={className}
      />
    );
  };

  return (
    <>
      <div
        id="navigation"
        className="fixed-top navbar-light bg-faded site-navigation"
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-2 col-md-3 col-sm-4">
              <div className="site-logo d-flex align-items-center h-100">
                <Link to="/" className="header-logo">
                  {renderLogo()}
                </Link>
              </div>
            </div>
            {/*- END Col */}
            <div className="col-lg-7 col-md-6 col-sm-4">
              <div className="header_right d-flex justify-content-between align-items-center h-100">
                {/* Desktop Menu */}
                <nav id="main-menu" className="desktop-menu">
                  <ul></ul>
                </nav>

                {/* Mobile menu button - positioned on the right */}
                <button
                  className="mobile-menu-button"
                  onClick={toggleMobileMenu}
                  aria-label="Toggle mobile menu"
                  aria-expanded={mobileMenuVisible}
                  aria-controls="mobile_menu"
                >
                  <span
                    className={`hamburger-line ${
                      mobileMenuVisible ? "open" : ""
                    }`}
                  ></span>
                  <span
                    className={`hamburger-line ${
                      mobileMenuVisible ? "open" : ""
                    }`}
                  ></span>
                  <span
                    className={`hamburger-line ${
                      mobileMenuVisible ? "open" : ""
                    }`}
                  ></span>
                </button>

                {/* Mobile menu */}
                {mobileMenuVisible && (
                  <div
                    id="mobile_menu"
                    className="mobile-menu"
                    role="navigation"
                    aria-label="Mobile navigation"
                  >
                    <ul>
                      {user ? (
                        <>
                          <li>
                            <Link
                              to="/user/dashboard"
                              className="nav-link"
                              onClick={toggleMobileMenu}
                            >
                              Dashboard
                            </Link>
                          </li>
                          <li>
                            {userData?.userRole === "Admin" && (
                              <Link
                                to="/admin/dashboard"
                                className="nav-link"
                                onClick={closeUserDropdown}
                              >
                                Admin Panel
                              </Link>
                            )}
                          </li>
                          <li>
                            <button
                              className="nav-link logout-button"
                              onClick={() => {
                                toggleMobileMenu();
                                handleSignout();
                              }}
                            >
                              Sign Out
                            </button>
                          </li>
                        </>
                      ) : (
                        <li>
                          <Link
                            to="/login"
                            className="nav-link"
                            onClick={toggleMobileMenu}
                          >
                            Login
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            {/*- END Col */}
            <div className="col-lg-3 col-md-3 col-sm-4">
              <div className="call_to_action d-flex justify-content-end align-items-center h-100">
                {user ? (
                  <div className="user-dropdown-container">
                    <button
                      className="user-avatar-button"
                      onClick={toggleUserDropdown}
                      aria-label="User menu"
                      aria-expanded={userDropdownOpen}
                      aria-haspopup="true"
                    >
                      <div className="user-avatar">{getUserInitials()}</div>
                      <span>My Account</span>
                    </button>

                    {userDropdownOpen && (
                      <div className="user-dropdown" role="menu">
                        <div className="user-info">
                          <div className="user-name">
                            {user?.displayName || "User"}
                          </div>
                          <div className="user-email">{user?.email}</div>
                        </div>
                        <div className="user-dropdown-menu">
                          <Link
                            to="/user/dashboard"
                            className="dropdown-item"
                            onClick={closeUserDropdown}
                            role="menuitem"
                          >
                            Dashboard
                          </Link>
                          {userData?.userRole === "Admin" && (
                            <Link
                              to="/admin/dashboard"
                              className="dropdown-item"
                              onClick={closeUserDropdown}
                              role="menuitem"
                            >
                              Admin Panel
                            </Link>
                          )}
                          <button
                            className="dropdown-item logout-button"
                            onClick={handleSignout}
                            role="menuitem"
                          >
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link to="/login" className="login-button">
                    Login
                  </Link>
                )}
              </div>
              {/*- END SOCIAL PROFILE */}
            </div>
            {/*- END Col */}
          </div>
          {/*- END ROW */}
        </div>
        {/*- END CONTAINER */}
      </div>

      <style jsx>{`
        .header_right {
          position: relative;
        }

        .desktop-menu {
          display: block;
        }

        .desktop-menu ul {
          list-style: none;
          display: flex;
          margin: 0;
          padding: 0;
        }

        .desktop-menu ul li {
          margin-right: 20px;
        }

        .desktop-menu ul li:last-child {
          margin-right: 0;
        }

        .user-dropdown-container {
          position: relative;
        }

        .user-avatar-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background-color 0.3s;
        }

        .user-avatar-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: #4a6cf7;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        .user-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 220px;
          background-color: #fff;
          border-radius: 8px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          overflow: hidden;
        }

        .user-info {
          padding: 15px;
          border-bottom: 1px solid #eee;
        }

        .user-name {
          font-weight: bold;
          margin-bottom: 5px;
        }

        .user-email {
          font-size: 0.85rem;
          color: #666;
        }

        .user-dropdown-menu {
          padding: 5px 0;
        }

        .dropdown-item {
          display: block;
          width: 100%;
          padding: 10px 15px;
          text-align: left;
          background: none;
          border: none;
          cursor: pointer;
          color: #333;
          text-decoration: none;
          transition: background-color 0.2s;
        }

        .dropdown-item:hover {
          background-color: #f5f5f5;
        }

        .logout-button {
          color: #e74c3c;
        }

        .login-button {
          display: inline-block;
          padding: 8px 20px;
          background-color: #4a6cf7;
          color: white;
          border-radius: 4px;
          text-decoration: none;
          font-weight: 500;
          transition: background-color 0.3s;
        }

        .login-button:hover {
          background-color: #3a5bd9;
        }

        .mobile-menu-button {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 30px;
          height: 21px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          z-index: 1000;
          margin-left: auto;
        }

        .hamburger-line {
          width: 100%;
          height: 3px;
          background-color: #333;
          transition: all 0.3s ease;
        }

        .hamburger-line.open:nth-child(1) {
          transform: translateY(9px) rotate(45deg);
        }

        .hamburger-line.open:nth-child(2) {
          opacity: 0;
        }

        .hamburger-line.open:nth-child(3) {
          transform: translateY(-9px) rotate(-45deg);
        }

        .mobile-menu {
          position: absolute;
          top: 100%;
          right: 0;
          width: 250px;
          background-color: #fff;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          z-index: 999;
          padding: 20px;
          border-radius: 8px;
          margin-top: 0; /* Removed margin-top to eliminate gap */
        }

        .mobile-menu ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .mobile-menu ul li {
          margin-bottom: 10px;
        }

        .mobile-menu ul li:last-child {
          margin-bottom: 0;
        }

        .mobile-menu .nav-link {
          display: block;
          padding: 10px 0;
          color: #333;
          text-decoration: none;
        }

        .mobile-menu .logout-button {
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          padding: 10px 0;
          color: #e74c3c;
          cursor: pointer;
        }

        @media (max-width: 991px) {
          .desktop-menu {
            display: none;
          }

          .mobile-menu-button {
            display: flex;
          }

          .user-dropdown-container {
            display: none;
          }

          .mobile-menu {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 576px) {
          .mobile-menu {
            width: 100%;
            max-width: none;
            right: 0;
            left: 0;
            border-radius: 0;
          }
        }
      `}</style>
    </>
  );
};

export default NavBar;
